import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ChatSlice } from "@/stores/slices/chat-slice"
import type { StoreApi, UseBoundStore } from "zustand"

import { sendDurableChatTurnSteerNoteApi } from "@/api/api"
import {
  dumpWebSocketRegistry,
  getSessionWebSocket,
} from "@/features/chat/utils/websocket-session-registry"
import { getFeatureConfig } from "@/hooks/feature-utils"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { logger } from "@/lib/logger"

import { type ExplorerSteerFeatureConfig, MAX_STEER_INPUT_CHARS } from "../types/steer-types"

/** Same seq + different payload: Cosmos already occupies this control id. */
const MAX_DURABLE_STEER_IDENTITY_RETRIES = 8

function isDurableSteerIdentityConflict(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false
  const candidate = error as { status?: unknown; code?: unknown }
  return candidate.status === 409 && candidate.code === "control_identity_conflict"
}

/**
 * Send a steer/cancel control frame over the running turn's WebSocket via the
 * session registry. Exact session-id lookups ONLY — steer/cancel frames carry
 * no session identity, so a last-registered fallback (as used by the
 * tool-approval path) could stop/steer whatever turn happens to own that
 * socket. Throws when no socket is registered for the given session(s) —
 * callers surface a toast and fall back locally.
 */
export function sendSteerControlFrame(
  sessionId: string | null,
  frame: Record<string, unknown>,
  fallbackSessionId?: string | null
): void {
  const candidateIds = [sessionId, fallbackSessionId].filter((value): value is string =>
    Boolean(value)
  )

  let client: ReturnType<typeof getSessionWebSocket> | undefined
  for (const candidate of candidateIds) {
    client = getSessionWebSocket(candidate)
    if (client) break
  }

  if (!client) {
    logger.error("Steer control frame WebSocket lookup failed", {
      frameType: frame.type,
      candidateIds,
    })
    dumpWebSocketRegistry("steer-control-frame-failure")
    throw new Error("WebSocket connection not found for session")
  }

  client.send(frame)
}

// ── Composer bridge ─────────────────────────────────────────────────────────
// ChatFooter owns the composer text but ChatFooterProps.onCancel takes no
// arguments, so stop-and-redirect can't receive the draft through props.
// Instead the mounted footer registers draft accessors AND its canonical send
// entry here (single slot — one chat composer is mounted at a time) and the
// steer cancel path in use-chat stashes the draft as pendingRedirect. The
// redirect auto-send goes back through `send` so it takes the exact same path
// as a user click (token-limit gate, prefix handling).

export type SteerComposerSendResult = boolean | void | undefined | Promise<unknown>

type SteerComposerBridge = {
  getText: () => string
  clearText: () => void
  setText: (text: string) => void
  /** The composer's canonical send path; returns false when a gate refused. */
  send: (text: string) => SteerComposerSendResult
}

let activeComposerBridge: SteerComposerBridge | null = null

/** Register the mounted composer's text access. Returns an unregister fn that
 *  only clears the slot if it still points at this registration. */
export function registerSteerComposer(bridge: SteerComposerBridge): () => void {
  activeComposerBridge = bridge
  return () => {
    if (activeComposerBridge === bridge) {
      activeComposerBridge = null
    }
  }
}

/** Read the composer draft without clearing it ("" when none/unregistered). */
export function peekSteerComposerText(): string {
  return activeComposerBridge?.getText() ?? ""
}

/** Clear the composer draft (no-op when no composer is registered). */
export function clearSteerComposerText(): void {
  activeComposerBridge?.clearText()
}

/** Put text back into the composer (no-op when no composer is registered) —
 *  used to restore a stop-and-redirect draft that could not be auto-sent. */
export function restoreSteerComposerText(text: string): void {
  activeComposerBridge?.setText(text)
}

/** The mounted composer's canonical send entry, or null when no composer is
 *  registered (e.g. the user navigated away from the chat page). */
export function getSteerComposerSend(): ((text: string) => SteerComposerSendResult) | null {
  return activeComposerBridge ? activeComposerBridge.send : null
}

/**
 * Effective `explorer_steer` sub-toggles for the current user. Both are false
 * whenever the feature flag is off or the user lacks access (`/config` already
 * filters features by access policy), so every consumer is inert by default.
 */
export function useSteerFeatureToggles(): {
  injectEnabled: boolean
  stopRedirectEnabled: boolean
} {
  const steerFeatureEnabled = useFeatureEnabled("explorer_steer")
  const { data: config } = useConfigQuery()
  const steerConfig = getFeatureConfig<ExplorerSteerFeatureConfig>(
    config?.features,
    "explorer_steer"
  )

  return {
    injectEnabled: steerFeatureEnabled && (steerConfig?.inject_enabled ?? true),
    stopRedirectEnabled: steerFeatureEnabled && (steerConfig?.stop_redirect_enabled ?? true),
  }
}

/**
 * Steer actions for the chat page (Stage 1 — mid-run injection). `sendSteerNote`
 * sends a `steer` frame for the running turn and tracks it as a pending chip in
 * the session's `steerNotes`; the `steer_status` frame handler settles the chip.
 * Returns false (composer keeps the text) when the note can't be sent.
 */
export function useSteerActions({
  useChatStore,
  getDurableToken,
}: {
  useChatStore: () => UseBoundStore<StoreApi<ChatSlice>>
  /** Auth token accessor for the durable transport. Omitted by callers that
   *  only ever run the WebSocket path (tests, legacy mounts). */
  getDurableToken?: () => string | undefined
}) {
  const { t } = useTranslation()
  const { injectEnabled, stopRedirectEnabled } = useSteerFeatureToggles()
  const chatStore = useChatStore()

  const sendSteerNote = useCallback(
    (content: string): boolean => {
      const trimmed = content.trim()
      if (!trimmed) return false

      const store = chatStore.getState()
      const sessionId = store.currentSessionId
      const sessionState = sessionId ? store.sessionStates[sessionId] : undefined
      // A durable turn is steerable on the strength of the turn itself: it runs
      // off the HTTP surface, which carries no WS control channel, so the
      // server never emits the `steer_ready` frame this gate keys on (#2814).
      const durableTurn = sessionState?.durableTurn ?? null
      if (!injectEnabled || !sessionId || !sessionState) return false
      if (!sessionState.steerReady && !durableTurn) return false

      if (trimmed.length > MAX_STEER_INPUT_CHARS) {
        toast.error(
          t("questioninput.steer_too_long", {
            defaultValue: "Steering notes are limited to {{max}} characters.",
            max: MAX_STEER_INPUT_CHARS,
          })
        )
        return false
      }

      const steerId = crypto.randomUUID()

      if (durableTurn) {
        // Sequence is per-turn and self-resetting: a stored counter from an
        // earlier turn cannot leak into this one. Do not consume `next` until
        // Cosmos has the control — `_deliver_steer_notes` stops at the first
        // missing seq, so a burned slot the store never occupied would strand
        // every later note. `add_control` is idempotent for the same id+payload.
        const stored = sessionState.durableSteerSeq
        const seq = stored && stored.turnId === durableTurn.turnId ? stored.next : 1
        store.updateSession(sessionId, {
          steerNotes: [
            ...sessionState.steerNotes,
            { steer_id: steerId, content: trimmed, status: "pending" },
          ],
        })
        const commitConsumedSeq = (consumedSeq: number) => {
          const latest = chatStore.getState().sessionStates[sessionId] as
            | typeof sessionState
            | undefined
          if (!latest) return
          const current = latest.durableSteerSeq
          const currentNext = current && current.turnId === durableTurn.turnId ? current.next : 1
          store.updateSession(sessionId, {
            durableSteerSeq: {
              turnId: durableTurn.turnId,
              next: Math.max(currentNext, consumedSeq + 1),
            },
          })
        }
        // Fire-and-forget, like the WebSocket send: the note's real outcome
        // arrives later as a `steer_status` replay event, which settles the
        // chip. Only a failure to STORE it is settled here.
        void (async () => {
          let sendSeq = seq
          try {
            for (let attempt = 0; attempt < MAX_DURABLE_STEER_IDENTITY_RETRIES; attempt += 1) {
              try {
                await sendDurableChatTurnSteerNoteApi(
                  durableTurn.sessionId,
                  { turnId: durableTurn.turnId, seq: sendSeq, content: trimmed, steerId },
                  getDurableToken?.()
                )
                commitConsumedSeq(sendSeq)
                return
              } catch (error: unknown) {
                if (!isDurableSteerIdentityConflict(error)) throw error
                // Occupied by a different payload: skip this seq so the gapless
                // drain cannot strand the retry, then post the next seq.
                commitConsumedSeq(sendSeq)
                sendSeq += 1
              }
            }
            throw new Error("Durable steer note identity-conflict retries exhausted")
          } catch (error: unknown) {
            logger.error("Failed to store durable steer note", error as Error)
            toast.error(
              t("questioninput.steer_send_failed", {
                defaultValue: "Couldn't reach the running response — the note was not sent.",
              })
            )
            // Re-read: the turn may have ended (and chips settled) while the
            // request was in flight. Mapping over the CURRENT array rather than
            // the captured one keeps this from resurrecting a cleared chip.
            const latest = chatStore.getState().sessionStates[sessionId] as
              | typeof sessionState
              | undefined
            if (!latest) return
            store.updateSession(sessionId, {
              steerNotes: latest.steerNotes.map((note) =>
                note.steer_id === steerId ? { ...note, status: "rejected" } : note
              ),
            })
          }
        })()
        return true
      }

      try {
        sendSteerControlFrame(sessionId, {
          type: "steer",
          steer_id: steerId,
          content: trimmed,
        })
      } catch (error) {
        logger.error("Failed to send steer note", error as Error)
        toast.error(
          t("questioninput.steer_send_failed", {
            defaultValue: "Couldn't reach the running response — the note was not sent.",
          })
        )
        // Surface the failure as a rejected chip (the note never left the
        // client) while the draft stays in the composer for a retry.
        store.updateSession(sessionId, {
          steerNotes: [
            ...sessionState.steerNotes,
            { steer_id: steerId, content: trimmed, status: "rejected" },
          ],
        })
        return false
      }

      store.updateSession(sessionId, {
        steerNotes: [
          ...sessionState.steerNotes,
          { steer_id: steerId, content: trimmed, status: "pending" },
        ],
      })
      return true
    },
    [chatStore, getDurableToken, injectEnabled, t]
  )

  return { injectEnabled, stopRedirectEnabled, sendSteerNote }
}
